import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Change,
  ChangeType,
  ChangeEntityType,
  ChangeStatus,
  ChangeDiff,
  ChangeStats,
  ChangeFilter,
  BatchChange,
  RevertChangeOperation,
  ContributionSource,
  ContributionSourceDetails,
} from '../types';

/**
 * RecentChange interface - UI-friendly format for displaying recent activity
 * (Merged from RecentChangesService)
 */
export interface RecentChange {
  id: string;
  type: 'document' | 'memory' | 'note' | 'comment' | 'research';
  title: string;
  description?: string;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  personId: string;
  personName?: string;
  metadata?: any;
}

/**
 * ChangeTrackingService - Handles all change tracking and approval operations
 *
 * Implements comprehensive change logging, approval workflow, and revert functionality
 * for maintaining data integrity and quality control in collaborative editing.
 */
export class ChangeTrackingService {
  private static COLLECTION_NAME = 'changes';
  private static BATCH_CHANGES_COLLECTION = 'batchChanges';

  /**
   * Log a new change to the changes collection
   *
   * @param {Partial<Change>} changeData - Change data to log
   * @param {string} userId - ID of user making the change
   * @param {string} userName - Display name of user
   * @param {boolean} isAdmin - Whether user is admin (for auto-approval)
   * @param {ContributionSource} source - Source of the change (default: 'user')
   * @param {ContributionSourceDetails} sourceDetails - Additional source details (optional)
   * @returns {Promise<Change>} Created change record
   * @throws {Error} If logging fails
   *
   * @example
   * const change = await ChangeTrackingService.logChange({
   *   entityType: 'person',
   *   entityId: 'person123',
   *   changeType: 'update',
   *   beforeState: oldPerson,
   *   afterState: newPerson,
   *   changedFields: ['firstName', 'birthDate']
   * }, 'user123', 'John Doe', false, 'user');
   */
  static async logChange(
    changeData: Partial<Change>,
    userId: string,
    userName: string,
    isAdmin: boolean = false,
    source: ContributionSource = 'user',
    sourceDetails?: ContributionSourceDetails
  ): Promise<Change> {
    try {
      // Validate required fields
      if (!changeData.entityType || !changeData.changeType) {
        throw new Error('entityType and changeType are required');
      }

      // Calculate diff if both states provided
      const changedFields = changeData.changedFields ||
        this.calculateChangedFields(changeData.beforeState, changeData.afterState);

      // Extract personId from afterState or beforeState if available
      const personId = changeData.afterState?.personId || changeData.beforeState?.personId || undefined;

      // Determine approval status
      const isAutoApproved = isAdmin;
      const status: ChangeStatus = isAutoApproved ? 'auto-approved' : 'pending';

      // Prepare change document
      const change: Omit<Change, 'id'> = {
        entityType: changeData.entityType,
        entityId: changeData.entityId || '',
        personId,
        changeType: changeData.changeType,
        beforeState: changeData.beforeState || null,
        afterState: changeData.afterState || null,
        changedFields,
        userId,
        userName,
        reason: changeData.reason,
        source: source || 'user', // Default to 'user' for backward compatibility
        sourceDetails: sourceDetails || {},
        status,
        isAutoApproved,
        autoApprovalReason: isAutoApproved ? 'Admin user' : undefined,
        batchId: changeData.batchId,
        batchDescription: changeData.batchDescription,
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Remove undefined fields deeply (Firestore doesn't accept undefined)
      const cleanedChange = this.deepRemoveUndefined(change);

      // Create in Firestore
      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), cleanedChange);

      return {
        id: docRef.id,
        ...change,
      } as Change;
    } catch (error: any) {
      console.error('Failed to log change:', error);
      throw new Error(`Failed to log change: ${error.message}`);
    }
  }

  /**
   * Get all pending changes awaiting approval
   *
   * @param {number} maxResults - Maximum number of results to return
   * @returns {Promise<Change[]>} Array of pending changes
   * @throws {Error} If query fails
   */
  static async getPendingChanges(maxResults: number = 100): Promise<Change[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );
    } catch (error: any) {
      console.error('Failed to get pending changes:', error);
      throw new Error(`Failed to get pending changes: ${error.message}`);
    }
  }

  /**
   * Get a specific change by ID
   *
   * @param {string} changeId - Change ID
   * @returns {Promise<Change | null>} Change record or null if not found
   * @throws {Error} If query fails
   */
  static async getChangeById(changeId: string): Promise<Change | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, changeId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.convertChangeTimestamps({
        id: docSnap.id,
        ...docSnap.data(),
      });
    } catch (error: any) {
      console.error('Failed to get change:', error);
      throw new Error(`Failed to get change: ${error.message}`);
    }
  }

  /**
   * Approve a pending change and apply it to the target entity
   *
   * @param {string} changeId - Change ID to approve
   * @param {string} reviewerId - ID of admin approving the change
   * @param {string} reviewerName - Display name of reviewer
   * @param {string} reviewNotes - Optional notes about the approval
   * @returns {Promise<void>}
   * @throws {Error} If approval fails or change not found
   *
   * @example
   * await ChangeTrackingService.approveChange(
   *   'change123',
   *   'admin456',
   *   'Admin User',
   *   'Looks good'
   * );
   */
  static async approveChange(
    changeId: string,
    reviewerId: string,
    reviewerName: string,
    reviewNotes?: string
  ): Promise<void> {
    try {
      // Get the change
      const change = await this.getChangeById(changeId);
      if (!change) {
        throw new Error('Change not found');
      }

      if (change.status !== 'pending') {
        throw new Error(`Change is not pending (current status: ${change.status})`);
      }

      // Update change status
      const changeRef = doc(db, this.COLLECTION_NAME, changeId);
      await updateDoc(changeRef, {
        status: 'approved',
        reviewedBy: reviewerId,
        reviewedAt: new Date(),
        reviewNotes,
        updatedAt: new Date(),
      });

      // Apply the change to the actual entity
      await this.applyChange(change);
    } catch (error: any) {
      console.error('Failed to approve change:', error);
      throw new Error(`Failed to approve change: ${error.message}`);
    }
  }

  /**
   * Reject a pending change
   *
   * @param {string} changeId - Change ID to reject
   * @param {string} reviewerId - ID of admin rejecting the change
   * @param {string} reviewerName - Display name of reviewer
   * @param {string} reviewNotes - Reason for rejection
   * @returns {Promise<void>}
   * @throws {Error} If rejection fails or change not found
   */
  static async rejectChange(
    changeId: string,
    reviewerId: string,
    reviewerName: string,
    reviewNotes: string
  ): Promise<void> {
    try {
      // Get the change
      const change = await this.getChangeById(changeId);
      if (!change) {
        throw new Error('Change not found');
      }

      if (change.status !== 'pending') {
        throw new Error(`Change is not pending (current status: ${change.status})`);
      }

      // Update change status
      const changeRef = doc(db, this.COLLECTION_NAME, changeId);
      await updateDoc(changeRef, {
        status: 'rejected',
        reviewedBy: reviewerId,
        reviewedAt: new Date(),
        reviewNotes,
        updatedAt: new Date(),
      });
    } catch (error: any) {
      console.error('Failed to reject change:', error);
      throw new Error(`Failed to reject change: ${error.message}`);
    }
  }

  /**
   * Revert an approved change back to its previous state
   *
   * @param {string} changeId - Change ID to revert
   * @param {string} userId - ID of user performing revert (must be admin)
   * @param {string} userName - Display name of user
   * @param {string} reason - Reason for reverting
   * @returns {Promise<void>}
   * @throws {Error} If revert fails or change cannot be reverted
   *
   * @example
   * await ChangeTrackingService.revertChange(
   *   'change123',
   *   'admin456',
   *   'Admin User',
   *   'Incorrect data - reverting to previous state'
   * );
   */
  static async revertChange(
    changeId: string,
    userId: string,
    userName: string,
    reason: string
  ): Promise<void> {
    try {
      // Get the original change
      const originalChange = await this.getChangeById(changeId);
      if (!originalChange) {
        throw new Error('Change not found');
      }

      if (originalChange.status !== 'approved' && originalChange.status !== 'auto-approved') {
        throw new Error('Can only revert approved changes');
      }

      // For create operations, we need to delete the entity
      // For update operations, we restore the beforeState
      // For delete operations, we restore the entity
      if (originalChange.changeType === 'create') {
        // Delete the created entity
        await this.deleteEntity(originalChange.entityType, originalChange.entityId);
      } else if (originalChange.changeType === 'update') {
        // Restore the beforeState
        await this.updateEntity(
          originalChange.entityType,
          originalChange.entityId,
          originalChange.beforeState
        );
      } else if (originalChange.changeType === 'delete') {
        // Restore the deleted entity
        await this.createEntity(
          originalChange.entityType,
          originalChange.entityId,
          originalChange.beforeState
        );
      }

      // Log the revert as a new change
      await this.logChange(
        {
          entityType: originalChange.entityType,
          entityId: originalChange.entityId,
          changeType: 'update',
          beforeState: originalChange.afterState,
          afterState: originalChange.beforeState,
          reason: `Revert: ${reason}`,
        },
        userId,
        userName,
        true // Admin operation
      );
    } catch (error: any) {
      console.error('Failed to revert change:', error);
      throw new Error(`Failed to revert change: ${error.message}`);
    }
  }

  /**
   * Get change history for a specific entity
   *
   * @param {ChangeEntityType} entityType - Type of entity
   * @param {string} entityId - Entity ID
   * @param {number} maxResults - Maximum results to return
   * @returns {Promise<Change[]>} Array of changes for the entity
   * @throws {Error} If query fails
   */
  static async getChangeHistory(
    entityType: ChangeEntityType,
    entityId: string,
    maxResults: number = 50
  ): Promise<Change[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('entityType', '==', entityType),
        where('entityId', '==', entityId),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );
    } catch (error: any) {
      console.error('Failed to get change history:', error);
      throw new Error(`Failed to get change history: ${error.message}`);
    }
  }

  /**
   * Get recent activity for a specific person
   * Returns all approved changes related to a person (documents, memories, notes, etc.)
   *
   * @param {string} personId - Person ID
   * @param {number} maxResults - Maximum results to return
   * @returns {Promise<Change[]>} Array of recent changes for the person
   * @throws {Error} If query fails
   */
  static async getRecentActivityForPerson(
    personId: string,
    maxResults: number = 20
  ): Promise<Change[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('personId', '==', personId),
        where('status', 'in', ['approved', 'auto-approved']),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );
    } catch (error: any) {
      console.error('Failed to get recent activity for person:', error);
      throw new Error(`Failed to get recent activity for person: ${error.message}`);
    }
  }

  /**
   * Get all changes made by a specific user
   *
   * @param {string} userId - User ID
   * @param {ChangeStatus} status - Optional status filter
   * @param {number} maxResults - Maximum results to return
   * @returns {Promise<Change[]>} Array of user's changes
   * @throws {Error} If query fails
   */
  static async getChangesForUser(
    userId: string,
    status?: ChangeStatus,
    maxResults: number = 100
  ): Promise<Change[]> {
    try {
      let q;
      if (status) {
        q = query(
          collection(db, this.COLLECTION_NAME),
          where('userId', '==', userId),
          where('status', '==', status),
          orderBy('createdAt', 'desc'),
          limit(maxResults)
        );
      } else {
        q = query(
          collection(db, this.COLLECTION_NAME),
          where('userId', '==', userId),
          orderBy('createdAt', 'desc'),
          limit(maxResults)
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );
    } catch (error: any) {
      console.error('Failed to get user changes:', error);
      throw new Error(`Failed to get user changes: ${error.message}`);
    }
  }

  /**
   * Get filtered changes based on criteria
   *
   * @param {ChangeFilter} filter - Filter criteria
   * @param {number} maxResults - Maximum results to return
   * @returns {Promise<Change[]>} Filtered changes
   * @throws {Error} If query fails
   */
  static async getFilteredChanges(
    filter: ChangeFilter,
    maxResults: number = 100
  ): Promise<Change[]> {
    try {
      let q = query(collection(db, this.COLLECTION_NAME));

      // Apply filters
      if (filter.entityType) {
        q = query(q, where('entityType', '==', filter.entityType));
      }
      if (filter.changeType) {
        q = query(q, where('changeType', '==', filter.changeType));
      }
      if (filter.status) {
        q = query(q, where('status', '==', filter.status));
      }
      if (filter.userId) {
        q = query(q, where('userId', '==', filter.userId));
      }
      if (filter.batchId) {
        q = query(q, where('batchId', '==', filter.batchId));
      }

      // Order and limit
      q = query(q, orderBy('createdAt', 'desc'), limit(maxResults));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );
    } catch (error: any) {
      console.error('Failed to get filtered changes:', error);
      throw new Error(`Failed to get filtered changes: ${error.message}`);
    }
  }

  /**
   * Get change statistics for analytics dashboard
   *
   * @returns {Promise<ChangeStats>} Change statistics
   * @throws {Error} If calculation fails
   */
  static async getChangeStats(): Promise<ChangeStats> {
    try {
      // Get all changes (in production, this should be optimized with aggregation)
      const allChanges = await getDocs(collection(db, this.COLLECTION_NAME));
      const changes = allChanges.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Change[];

      // Calculate statistics
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const stats: ChangeStats = {
        totalChanges: changes.length,
        pendingChanges: changes.filter(c => c.status === 'pending').length,
        approvedChanges: changes.filter(c => c.status === 'approved' || c.status === 'auto-approved').length,
        rejectedChanges: changes.filter(c => c.status === 'rejected').length,
        recentChanges: changes.filter(c => new Date(c.createdAt) > oneDayAgo).length,
        changesByType: this.countByField(changes, 'changeType'),
        changesByEntity: this.countByField(changes, 'entityType'),
        changesByUser: this.countChangesByUser(changes),
        averageApprovalTime: this.calculateAverageApprovalTime(changes),
      };

      return stats;
    } catch (error: any) {
      console.error('Failed to get change stats:', error);
      throw new Error(`Failed to get change stats: ${error.message}`);
    }
  }

  /**
   * Filter changes by contribution source
   *
   * @param {Change[]} changes - Array of changes to filter
   * @param {ContributionSource | ContributionSource[]} sources - Source(s) to filter by
   * @returns {Change[]} Filtered changes
   */
  static filterBySource(
    changes: Change[],
    sources: ContributionSource | ContributionSource[]
  ): Change[] {
    const sourceArray = Array.isArray(sources) ? sources : [sources];
    return changes.filter(change => sourceArray.includes(change.source));
  }

  /**
   * Get change statistics grouped by contribution source
   *
   * @param {Change[]} changes - Array of changes to analyze
   * @returns {Record<ContributionSource, number>} Count of changes per source
   */
  static getChangeStatisticsBySource(
    changes: Change[]
  ): Record<ContributionSource, number> {
    const stats: Record<string, number> = {};

    // Initialize all sources with 0
    const allSources: ContributionSource[] = [
      'user',
      'system',
      'api-ancestry',
      'api-familysearch',
      'import-gedcom',
      'import-csv',
      'merge',
      'admin-correction',
    ];

    allSources.forEach(source => {
      stats[source] = 0;
    });

    // Count changes by source
    changes.forEach(change => {
      const source = change.source || 'user'; // Default to 'user' for legacy changes
      stats[source] = (stats[source] || 0) + 1;
    });

    return stats as Record<ContributionSource, number>;
  }

  /**
   * Get all changes with optional source filtering
   *
   * @param {ContributionSource | ContributionSource[]} sources - Optional source filter
   * @param {number} maxResults - Maximum results to return
   * @returns {Promise<Change[]>} All changes (optionally filtered)
   */
  static async getAllChanges(
    sources?: ContributionSource | ContributionSource[],
    maxResults: number = 500
  ): Promise<Change[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('createdAt', 'desc'),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      let changes = snapshot.docs.map(doc =>
        this.convertChangeTimestamps({
          id: doc.id,
          ...doc.data(),
        })
      );

      // Apply source filter if provided
      if (sources) {
        changes = this.filterBySource(changes, sources);
      }

      return changes;
    } catch (error: any) {
      console.error('Failed to get all changes:', error);
      throw new Error(`Failed to get all changes: ${error.message}`);
    }
  }

  /**
   * Calculate diff between before and after states
   *
   * @param {any} beforeState - Previous state
   * @param {any} afterState - New state
   * @returns {ChangeDiff[]} Array of field-level diffs
   */
  static calculateDiff(beforeState: any, afterState: any): ChangeDiff[] {
    const diffs: ChangeDiff[] = [];

    if (!beforeState && !afterState) {
      return diffs;
    }

    // If one state is null, all fields are added or removed
    if (!beforeState) {
      Object.keys(afterState).forEach(key => {
        diffs.push({
          field: key,
          fieldLabel: this.formatFieldLabel(key),
          oldValue: null,
          newValue: afterState[key],
          changeType: 'added',
        });
      });
      return diffs;
    }

    if (!afterState) {
      Object.keys(beforeState).forEach(key => {
        diffs.push({
          field: key,
          fieldLabel: this.formatFieldLabel(key),
          oldValue: beforeState[key],
          newValue: null,
          changeType: 'removed',
        });
      });
      return diffs;
    }

    // Compare fields
    const allKeys = new Set([...Object.keys(beforeState), ...Object.keys(afterState)]);
    allKeys.forEach(key => {
      // Skip metadata fields
      if (['createdAt', 'updatedAt', 'id'].includes(key)) {
        return;
      }

      const oldValue = beforeState[key];
      const newValue = afterState[key];

      // Check if values are different
      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        let changeType: 'added' | 'removed' | 'modified';
        if (oldValue === undefined || oldValue === null) {
          changeType = 'added';
        } else if (newValue === undefined || newValue === null) {
          changeType = 'removed';
        } else {
          changeType = 'modified';
        }

        diffs.push({
          field: key,
          fieldLabel: this.formatFieldLabel(key),
          oldValue,
          newValue,
          changeType,
        });
      }
    });

    return diffs;
  }

  // ========== RECENT CHANGES (MERGED FROM RecentChangesService) ==========

  /**
   * Fetch recent changes for a specific person
   * Returns a UI-friendly format for displaying recent activity
   *
   * @param personId - ID of the person to fetch changes for
   * @param maxItems - Maximum number of items to return (default: 10)
   * @returns Promise<RecentChange[]> Array of recent changes
   */
  static async getRecentChangesForPerson(
    personId: string,
    maxItems: number = 10
  ): Promise<RecentChange[]> {
    try {
      // Query approved changes from ChangeTrackingService
      const changes = await this.getRecentActivityForPerson(personId, maxItems);

      // Transform Change objects to RecentChange objects
      return changes
        .filter(change => this.isProfileTabChange(change.entityType))
        .map(change => this.transformChangeToRecentChange(change))
        .filter((rc): rc is RecentChange => rc !== null);
    } catch (error) {
      console.error('Error fetching recent changes:', error);
      return [];
    }
  }

  /**
   * Fetch recent changes across all people (for dashboard/home page)
   *
   * @param maxItems - Maximum number of items to return (default: 20)
   * @returns Promise<RecentChange[]> Array of recent changes
   */
  static async getAllRecentChanges(maxItems: number = 20): Promise<RecentChange[]> {
    try {
      // This would require a different query without personId filter
      // Can be extended later for global dashboard
      return [];
    } catch (error) {
      console.error('Error fetching all recent changes:', error);
      return [];
    }
  }

  /**
   * Check if entity type is a profile tab change
   */
  private static isProfileTabChange(entityType: ChangeEntityType): boolean {
    return ['document', 'memory', 'note', 'profileComment', 'research'].includes(entityType);
  }

  /**
   * Transform Change to RecentChange
   */
  private static transformChangeToRecentChange(change: Change): RecentChange | null {
    const data = change.afterState;
    if (!data) return null;

    const baseChange = {
      id: change.entityId,
      createdBy: change.userId,
      createdByName: change.userName,
      createdAt: change.createdAt instanceof Date ? change.createdAt : new Date(change.createdAt),
      personId: change.personId || data.personId || '',
      personName: data.personName,
    };

    switch (change.entityType) {
      case 'document':
        return {
          ...baseChange,
          type: 'document',
          title: data.title || 'Untitled Document',
          description: data.description,
          metadata: { type: data.type, fileSize: data.fileSize },
        };

      case 'memory':
        return {
          ...baseChange,
          type: 'memory',
          title: data.title || 'Untitled Memory',
          description: data.excerpt,
          metadata: {
            category: data.category,
            hasAudio: !!data.audioURL,
            hasVideo: !!data.videoURL
          },
        };

      case 'note':
        return {
          ...baseChange,
          type: 'note',
          title: data.content?.substring(0, 100) + (data.content?.length > 100 ? '...' : ''),
          description: undefined,
          metadata: { tags: data.tags, isPinned: data.isPinned },
        };

      case 'profileComment':
        return {
          ...baseChange,
          type: 'comment',
          title: data.content?.substring(0, 100) + (data.content?.length > 100 ? '...' : ''),
          description: undefined,
          metadata: { likeCount: data.likeCount || 0, replyCount: data.replyCount || 0 },
        };

      case 'research':
        return {
          ...baseChange,
          type: 'research',
          title: data.title || 'Untitled Research',
          description: data.objective,
          metadata: {
            type: data.type,
            status: data.status,
            completionPercentage: data.completionPercentage || 0,
          },
        };

      case 'lifeEvent':
        return {
          ...baseChange,
          type: 'document', // Reusing document type for now as it's close
          title: `${data.type || 'Life Event'}`,
          description: data.description,
          metadata: { type: data.type, date: data.date },
        };

      default:
        return null;
    }
  }

  // ===== Private Helper Methods =====

  /**
   * Apply an approved change to the actual entity
   */
  private static async applyChange(change: Change): Promise<void> {
    try {
      if (change.changeType === 'create') {
        await this.createEntity(change.entityType, change.entityId, change.afterState);
      } else if (change.changeType === 'update') {
        await this.updateEntity(change.entityType, change.entityId, change.afterState);
      } else if (change.changeType === 'delete') {
        await this.deleteEntity(change.entityType, change.entityId);
      }
    } catch (error: any) {
      console.error('Failed to apply change:', error);
      throw new Error(`Failed to apply change: ${error.message}`);
    }
  }

  /**
   * Create an entity in its collection
   */
  private static async createEntity(
    entityType: ChangeEntityType,
    entityId: string,
    data: any
  ): Promise<void> {
    const collectionName = this.getCollectionName(entityType);
    const docRef = doc(db, collectionName, entityId);
    await updateDoc(docRef, data);
  }

  /**
   * Update an entity in its collection
   */
  private static async updateEntity(
    entityType: ChangeEntityType,
    entityId: string,
    data: any
  ): Promise<void> {
    const collectionName = this.getCollectionName(entityType);
    const docRef = doc(db, collectionName, entityId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: new Date(),
    });
  }

  /**
   * Delete (soft delete) an entity
   */
  private static async deleteEntity(
    entityType: ChangeEntityType,
    entityId: string
  ): Promise<void> {
    const collectionName = this.getCollectionName(entityType);
    const docRef = doc(db, collectionName, entityId);
    await updateDoc(docRef, {
      isDeleted: true,
      updatedAt: new Date(),
    });
  }

  /**
   * Get Firestore collection name for entity type
   */
  private static getCollectionName(entityType: ChangeEntityType): string {
    const collectionMap: Record<ChangeEntityType, string> = {
      person: 'persons',
      relationship: 'relationships',
      lifeEvent: 'lifeEvents',
      photo: 'photos',
      comment: 'comments',
      user: 'users',
      album: 'albums',
      story: 'stories',
      document: 'documents',
      memory: 'memories',
      note: 'notes',
      profileComment: 'profileComments',
      research: 'researchNotes',
    };
    return collectionMap[entityType];
  }

  /**
   * Calculate which fields changed between two states
   */
  private static calculateChangedFields(beforeState: any, afterState: any): string[] {
    const diffs = this.calculateDiff(beforeState, afterState);
    return diffs.map(diff => diff.field);
  }

  /**
   * Format field name into human-readable label
   */
  private static formatFieldLabel(fieldName: string): string {
    // Convert camelCase to Title Case
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /**
   * Count items by a specific field
   */
  private static countByField(changes: Change[], field: keyof Change): Record<string, number> {
    const counts: Record<string, number> = {};
    changes.forEach(change => {
      const value = change[field] as string;
      counts[value] = (counts[value] || 0) + 1;
    });
    return counts;
  }

  /**
   * Count changes by user
   */
  private static countChangesByUser(changes: Change[]): Array<{
    userId: string;
    userName: string;
    count: number;
  }> {
    const userMap = new Map<string, { userId: string; userName: string; count: number }>();

    changes.forEach(change => {
      const existing = userMap.get(change.userId);
      if (existing) {
        existing.count++;
      } else {
        userMap.set(change.userId, {
          userId: change.userId,
          userName: change.userName,
          count: 1,
        });
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.count - a.count);
  }

  /**
   * Calculate average approval time in hours
   */
  private static calculateAverageApprovalTime(changes: Change[]): number {
    const approvedChanges = changes.filter(
      c => (c.status === 'approved' || c.status === 'rejected') && c.reviewedAt
    );

    if (approvedChanges.length === 0) {
      return 0;
    }

    const totalHours = approvedChanges.reduce((sum, change) => {
      const created = new Date(change.createdAt).getTime();
      const reviewed = new Date(change.reviewedAt!).getTime();
      const hours = (reviewed - created) / (1000 * 60 * 60);
      return sum + hours;
    }, 0);

    return totalHours / approvedChanges.length;
  }

  /**
   * Recursively remove undefined fields from an object
   * Firestore does not accept undefined values - they must be null or omitted
   */
  private static deepRemoveUndefined(obj: any): any {
    if (obj === null || obj === undefined) {
      return null;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.deepRemoveUndefined(item));
    }

    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const cleaned: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] !== undefined) {
          cleaned[key] = this.deepRemoveUndefined(obj[key]);
        }
      }
      return cleaned;
    }

    return obj;
  }

  /**
   * Convert Firestore Timestamp fields to Date objects in a Change record
   */
  private static convertChangeTimestamps(data: any): Change {
    return {
      ...data,
      createdAt: this.timestampToDate(data.createdAt),
      updatedAt: this.timestampToDate(data.updatedAt),
      reviewedAt: data.reviewedAt ? this.timestampToDate(data.reviewedAt) : undefined,
      beforeState: data.beforeState ? this.convertNestedTimestamps(data.beforeState) : null,
      afterState: data.afterState ? this.convertNestedTimestamps(data.afterState) : null,
    } as Change;
  }

  /**
   * Recursively convert Firestore Timestamps to Date objects in nested structures
   */
  private static convertNestedTimestamps(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Check if it's a Firestore Timestamp
    if (obj && typeof obj.toDate === 'function') {
      return obj.toDate();
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.convertNestedTimestamps(item));
    }

    // Handle objects
    if (typeof obj === 'object' && !(obj instanceof Date)) {
      const converted: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          converted[key] = this.convertNestedTimestamps(obj[key]);
        }
      }
      return converted;
    }

    return obj;
  }

  /**
   * Convert Firestore Timestamp to Date
   */
  private static timestampToDate(timestamp: any): Date {
    if (!timestamp) return new Date();
    if (timestamp instanceof Date) return timestamp;
    if (timestamp.toDate) return timestamp.toDate();
    if (typeof timestamp === 'string') return new Date(timestamp);
    if (typeof timestamp === 'number') return new Date(timestamp);
    return new Date();
  }
}
